import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { jwtDecode } from 'jwt-decode';
import { PatientVerificationData, StaffVerificationData } from './models/verification.interface';
import { UserResponseType } from './models/questionnaire.interface';
import {
  UpdateInteractionPatchType,
  InteractionEditStatusEnum,
} from './models/interactions.interface';
import {
  UpdateFeedbackInteractionPatchType,
  AnsweredFeedbackQuestions,
} from './models/feedback_survey.interface';
import { Checkbox } from 'antd';
import type { GetProp } from 'antd';
import { COLORS } from 'themes/colors';
import { Token } from 'models/token.interface';

export const patientVerificationDataAtom = atom<PatientVerificationData>({
  facility: '',
  firstName: '',
  patientId: '',
  lastName: '',
  careProviderId: 0,
  careProviderCoreId: '',
  careProviderName: '',
  careProviderSlug: '',
});

export const staffVerificationDataAtom = atom<StaffVerificationData>({
  staffMemberId: '',
  firstName: '',
  lastName: '',
  careProviderId: 0,
  careProviderCoreId: '',
  careProviderName: '',
  careProviderSlug: '',
});

// This token uses sessionStorage, so it survives a page refresh in the same tab, but is isolated per tab.
// Clearing its token (logout, idle timeout, 404/slug-mismatch) in another tab cannot wipe an active session.
export const verificationTokenAtom = atomWithStorage(
  'verification_token',
  '',
  createJSONStorage<string>(() => sessionStorage),
  { getOnInit: true },
);

// Derived atom — decodes verificationTokenAtom synchronously on read.
// Always in sync with the token, including on page refresh.
export const decodedTokenAtom = atom<Token | null>((get) => {
  const token = get(verificationTokenAtom);
  if (!token) return null;
  try {
    return jwtDecode<Token>(token);
  }
  catch {
    return null;
  }
});

// Normalized user identity — resolves whichever user type is present in the token.
export const currentUserAtom = atom((get) => {
  const token = get(decodedTokenAtom);
  if (!token) return null;
  return {
    userId: token.patientId ?? token.staffMemberId,
    userFirstName: token.patientFirstName ?? token.staffFirstName,
  };
});

export const singleSelectResponseAtom = atom<UserResponseType>({
  questionId: 0,
  answers: [],
});

export const multiSelectResponseAtom = atom<UserResponseType>({
  questionId: 0,
  answers: [],
});

export const multiSelectValuesAtom = atom<
  GetProp<typeof Checkbox.Group, 'value'>
>([]);

export const updateSurveyAtom = atom<UpdateInteractionPatchType | null>({
  id: 0,
  editStatus: InteractionEditStatusEnum.open,
  expired: false,
  questionViewLog: [],
  responses: [],
});

export const updateFeedbackSurveyAtom
  = atom<UpdateFeedbackInteractionPatchType>({
    responses: [],
  });

export const careProviderConfiguration = atom({
  logo: 'cipherLogo',
  botAvatar: 'cipherAvatar',
  botName: '',
  colorPrimary: COLORS.primary,
  avatarFillColor: COLORS.cipherGreenL0,
  colorBgHeader: '',
  landingPageBackgroundColor: '#e0fff6',
});
export const interactionIdAtom = atom<number | null>(null);

export const currentQuestionIndexAtom = atom(0);
export const currentFeedbackQuestionIndexAtom = atom(0);

export const answeredFeedbackQuestionsAtom = atom<AnsweredFeedbackQuestions[]>(
  [],
);

export const feedbackSurveyCompleteAtom = atom(false);
export const enableResumingSurveysAtom = atom(false);

// Language selection (from provider config)
export interface LanguageOption {
  name: string;
  code: string;
  nativeText?: string;
  flag?: string;
}

export const languagesAtom = atom<LanguageOption[]>([]);
export const selectedLanguageAtom = atomWithStorage<string>('selected_language', 'en');
